import { useEffect, useState, useMemo } from 'react';
import { ColumnDef } from '@tanstack/react-table';
import AdvanceTable from 'components/base/AdvanceTable';
import AdvanceTableFooter from 'components/base/AdvanceTableFooter';
import useAdvanceTable from 'hooks/useAdvanceTable';
import AdvanceTableProvider from 'providers/AdvanceTableProvider';
import { getMyDevices, Asset } from 'api/assetsAPI';
import PhoenixLoader from 'components/common/PhoenixLoader';
import FeatherIcon from 'feather-icons-react';
import ViewAssetModal from 'components/modals/ViewAssetModal';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faLaptop,
  faBuilding,
  faTag,
  faTags,
  faCube,
  faChartBar,
  faWrench
} from '@fortawesome/free-solid-svg-icons';

interface MyDevicesTableProps {
  searchQuery?: string;
}

// Extended interface for display purposes
interface DisplayDevice extends Omit<Asset, 'purchase_cost'> {
  purchase_cost: number;
  deviceName: string;
  serialNumber: string;
  category: string;
  assignedTo?: string;
}

const MyDevicesTable = ({ searchQuery = '' }: MyDevicesTableProps) => {
  const [data, setData] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [viewAsset, setViewAsset] = useState<DisplayDevice | null>(null);

  useEffect(() => {
    loadMyDevices();
  }, []);

  const loadMyDevices = async () => {
    setLoading(true);
    setError(null);
    try {
      const devices = await getMyDevices();
      setData(devices);
    } catch (err) {
      console.error('Failed to load my devices:', err);
      setError('Failed to load devices. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Transform backend data to frontend format
  const transformedData = useMemo((): DisplayDevice[] => {
    return data.map(asset => ({
      ...asset,
      purchase_cost: parseFloat(asset.purchase_cost) || 0,
      deviceName: asset.name,
      serialNumber: asset.serial_number,
      category: asset.category_name || 'Uncategorized',
      assignedTo: asset.current_holder_name
    }));
  }, [data]);

  const filteredData = useMemo(() => {
    if (!searchQuery.trim()) return transformedData;
    const query = searchQuery.toLowerCase().trim();
    return transformedData.filter(
      asset =>
        asset.deviceName.toLowerCase().includes(query) ||
        asset.category.toLowerCase().includes(query) ||
        asset.brand.toLowerCase().includes(query) ||
        asset.model.toLowerCase().includes(query) ||
        (asset.company_name && asset.company_name.toLowerCase().includes(query))
    );
  }, [transformedData, searchQuery]);

  const handleView = (asset: DisplayDevice) => {
    setViewAsset(asset);
  };

  const handleRetry = () => {
    loadMyDevices();
  };

  const table = useAdvanceTable({
    data: filteredData,
    columns: getColumns(handleView),
    pageSize: 10,
    pagination: true,
    sortable: true,
    selection: false
  });

  return (
    <AdvanceTableProvider {...table}>
      <div className="border-top border-translucent">
        {loading ? (
          <div className="text-center p-4">
            <PhoenixLoader />
          </div>
        ) : error ? (
          <div className="p-4 text-center">
            <div className="text-danger mb-2">Error: {error}</div>
            <button className="btn btn-primary btn-sm" onClick={handleRetry}>
              Retry
            </button>
          </div>
        ) : (
          <>
            {filteredData.length === 0 ? (
              <div className="text-center p-4">
                {searchQuery ? (
                  <div>
                    <p>No devices found matching "{searchQuery}"</p>
                  </div>
                ) : (
                  <div>
                    <p>No devices assigned to you</p>
                    <button
                      className="btn btn-primary btn-sm mt-2"
                      onClick={handleRetry}
                    >
                      Refresh
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <div className="px-3 py-2 bg-light border-bottom">
                  <small className="text-muted">
                    Showing {filteredData.length} device
                    {filteredData.length !== 1 ? 's' : ''}
                    {searchQuery && ` matching "${searchQuery}"`}
                  </small>
                </div>
                <AdvanceTable
                  tableProps={{
                    className:
                      'phoenix-table fs-8 border-start border-end rounded'
                  }}
                  rowClassName="hover-actions-trigger btn-reveal-trigger"
                />
                <AdvanceTableFooter pagination className="py-4" />
              </>
            )}
          </>
        )}
      </div>

      {viewAsset && (
        <ViewAssetModal
          show={!!viewAsset}
          handleClose={() => setViewAsset(null)}
          asset={viewAsset}
        />
      )}
    </AdvanceTableProvider>
  );
};

export default MyDevicesTable;

// Column definitions with proper typing
function getColumns(
  onView: (asset: DisplayDevice) => void
): ColumnDef<DisplayDevice>[] {
  return [
    {
      id: 'sn',
      header: 'SN',
      cell: ({ row }) => <span className="fw-semibold">{row.index + 1}</span>,
      meta: {
        headerProps: {
          style: { width: '80px' }
        },
        cellProps: {}
      }
    },
    {
      accessorKey: 'deviceName',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faLaptop} className="me-2 text-primary" />
          Asset Name
        </span>
      ),
      cell: ({ row: { original } }) => (
        <button
          className="btn btn-link p-0 fw-semibold text-decoration-none text-start"
          onClick={() => onView(original)}
        >
          {original.deviceName}
        </button>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      accessorKey: 'company_name',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faBuilding} className="me-2 text-primary" />
          Company
        </span>
      ),
      cell: ({ row: { original } }) => (
        <span className="text-body">{original.company_name || 'N/A'}</span>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      accessorKey: 'category',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faTag} className="me-2 text-primary" />
          Category
        </span>
      ),
      cell: ({ row: { original } }) => (
        <span className="text-body">{original.category}</span>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      accessorKey: 'brand',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faTags} className="me-2 text-primary" />
          Brand
        </span>
      ),
      cell: ({ row: { original } }) => (
        <span className="text-body">{original.brand}</span>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      accessorKey: 'model',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faCube} className="me-2 text-primary" />
          Model
        </span>
      ),
      cell: ({ row: { original } }) => (
        <span className="text-body">{original.model}</span>
      ),
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    // {
    //   accessorKey: 'status',
    //   header: 'Status',
    //   cell: ({ row: { original } }) => {
    //     const statusColors: Record<string, string> = {
    //       available: 'bg-success',
    //       assigned: 'bg-primary',
    //       maintenance: 'bg-warning',
    //       retired: 'bg-danger'
    //     };

    //     return (
    //       <span
    //         className={`badge ${
    //           statusColors[original.status] || 'bg-secondary'
    //         }`}
    //       >
    //         {original.status.charAt(0).toUpperCase() + original.status.slice(1)}
    //       </span>
    //     );
    //   },
    //   meta: {
    //     headerProps: {
    //       className: 'ps-2 bg-white py-3 text-uppercase'
    //     },
    //     cellProps: { className: 'ps-2' }
    //   }
    // },
    // {
    //   accessorKey: 'condition',
    //   header: 'Condition',
    //   cell: ({ row: { original } }) => {
    //     const conditionColors: Record<string, string> = {
    //       excellent: 'bg-success',
    //       good: 'bg-info',
    //       fair: 'bg-warning',
    //       poor: 'bg-danger'
    //     };

    //     return (
    //       <span
    //         className={`badge ${
    //           conditionColors[original.condition] || 'bg-secondary'
    //         }`}
    //       >
    //         {original.condition.charAt(0).toUpperCase() +
    //           original.condition.slice(1)}
    //       </span>
    //     );
    //   },
    //   meta: {
    //     headerProps: {
    //       className: 'ps-2 bg-white py-3 text-uppercase'
    //     },
    //     cellProps: { className: 'ps-2' }
    //   }
    // }
    {
      accessorKey: 'status',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faChartBar} className="me-2 text-primary" />
          Status
        </span>
      ),
      cell: ({ row: { original } }) => {
        const statusConfig: Record<
          string,
          { className: string; icon: string }
        > = {
          available: {
            className: 'badge-phoenix-success',
            icon: 'check'
          },
          assigned: {
            className: 'badge-phoenix-primary',
            icon: 'package'
          },
          maintenance: {
            className: 'badge-phoenix-warning',
            icon: 'alert-octagon'
          },
          retired: {
            className: 'badge-phoenix-danger',
            icon: 'x'
          }
        };

        const config = statusConfig[original.status] || {
          className: 'badge-phoenix-secondary',
          icon: 'info'
        };

        const label =
          original.status.charAt(0).toUpperCase() + original.status.slice(1);

        return (
          <span className={`badge badge-phoenix fs-10 ${config.className}`}>
            <span className="badge-label">{label}</span>
            <FeatherIcon icon={config.icon} size={12} className="ms-1" />
          </span>
        );
      },
      meta: {
        headerProps: {},
        cellProps: {}
      }
    },
    {
      accessorKey: 'condition',
      header: () => (
        <span>
          <FontAwesomeIcon icon={faWrench} className="me-2 text-primary" />
          Condition
        </span>
      ),
      cell: ({ row: { original } }) => {
        const conditionConfig: Record<
          string,
          { className: string; icon: string }
        > = {
          excellent: {
            className: 'badge-phoenix-success',
            icon: 'award'
          },
          good: {
            className: 'badge-phoenix-info',
            icon: 'thumbs-up'
          },
          fair: {
            className: 'badge-phoenix-warning',
            icon: 'alert-triangle'
          },
          poor: {
            className: 'badge-phoenix-danger',
            icon: 'thumbs-down'
          }
        };

        const config = conditionConfig[original.condition] || {
          className: 'badge-phoenix-secondary',
          icon: 'help-circle'
        };

        const label =
          original.condition.charAt(0).toUpperCase() +
          original.condition.slice(1);

        return (
          <span className={`badge badge-phoenix fs-10 ${config.className}`}>
            <span className="badge-label">{label}</span>
            <FeatherIcon icon={config.icon} size={12} className="ms-1" />
          </span>
        );
      },
      meta: {
        headerProps: {},
        cellProps: {}
      }
    }
  ];
}
